import React, { useEffect, useState } from "react";
import { Maximize2, X } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

export function GameBannerCarousel({ banner }) {
  const [selectedImage, setSelectedImage] = useState(null);
  const closeViewer = () => setSelectedImage(null);
  useEffect(() => {
    const onKeyDown = (event) => { if (event.key === "Escape") closeViewer(); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);
  if (!banner || !Array.isArray(banner.images) || banner.images.length === 0) return null;
  return (
    <div
      data-testid="game-banner-carousel"
      className={`relative w-full max-w-5xl mx-auto rounded-xl sm:rounded-2xl overflow-hidden border ${banner.fire_effect ? "border-orange-300 shadow-[0_0_12px_rgba(255,70,0,.9),0_0_30px_rgba(255,155,0,.65)]" : "border-[var(--ag7-border)] shadow-[0_16px_44px_-18px_rgba(0,0,0,0.9),0_0_20px_rgba(147,51,234,0.12)]"} bg-[var(--ag7-surface)] mb-2 sm:mb-4`}
    >
      <Swiper
        modules={[Autoplay, Pagination]}
        autoplay={{ delay: 3800, disableOnInteraction: false, pauseOnMouseEnter: true }}
        pagination={{ clickable: true }}
        loop={banner.images.length > 1}
        speed={600}
        className="w-full h-24 sm:h-64 md:h-72 lg:h-80"
      >
        {banner.images?.map((url, i) => (
          <SwiperSlide key={url}>
            {/* Full-bleed poster: horizontal banners now fill 100% of the
                card width (object-cover). The blurred backdrop is dropped —
                it was there only to letterbox vertical 4:5 posters, but the
                store admin uploads landscape banners so the extra layer just
                cropped the image visually. */}
            <button
              type="button"
              onClick={() => setSelectedImage({ url, index: i })}
              data-testid={`game-banner-open-${i}`}
              aria-label={`Ver imagen completa de ${banner.title}`}
              className="group relative block h-full w-full cursor-zoom-in"
            >
              <img
                src={url}
                alt={`${banner.title} ${i + 1}`}
                data-testid={`game-banner-slide-${i}`}
                className="h-full w-full select-none object-cover"
                style={{ imageRendering: "-webkit-optimize-contrast" }}
                loading="lazy"
                draggable={false}
              />
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-[background-color,opacity] duration-200 group-hover:bg-black/45 group-hover:opacity-100">
                <span className="inline-flex items-center gap-2 border border-white/50 bg-black/55 px-3 py-2 text-xs font-bold text-white"><Maximize2 className="h-4 w-4" />Ver imagen completa</span>
              </span>
            </button>
          </SwiperSlide>
        ))}
      </Swiper>
      {banner.fire_effect && <>
        <div data-testid="game-banner-fire-frame" aria-hidden="true" className="pointer-events-none absolute inset-0 z-[6] rounded-xl sm:rounded-2xl border-[3px] border-orange-400/90 shadow-[inset_0_0_14px_rgba(255,38,0,.85),0_0_18px_rgba(255,83,0,.95),0_0_34px_rgba(255,174,0,.6)] animate-pulse" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-2 inset-x-3 z-[7] h-5 bg-[radial-gradient(ellipse_at_8%_100%,#ff3c00_0_18%,transparent_19%),radial-gradient(ellipse_at_25%_100%,#ffd04b_0_13%,transparent_14%),radial-gradient(ellipse_at_43%_100%,#ff5100_0_20%,transparent_21%),radial-gradient(ellipse_at_61%_100%,#ffe36b_0_15%,transparent_16%),radial-gradient(ellipse_at_79%_100%,#ff3900_0_18%,transparent_19%),radial-gradient(ellipse_at_96%_100%,#ffb000_0_14%,transparent_15%)] blur-[2px] opacity-95 motion-reduce:animate-none animate-pulse" />
      </>}
      {/* Text overlay pinned bottom-left, using theme colors */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 p-2 sm:p-3 bg-gradient-to-t from-black/85 via-black/40 to-transparent">
        <h3
          data-testid="game-banner-title"
          className="font-heading text-white font-extrabold text-xs sm:text-sm md:text-base leading-tight drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)]"
        >
          {banner.title}
        </h3>
        {banner.description && (
          <p
            data-testid="game-banner-description"
            className="text-zinc-200 text-[10px] sm:text-[11px] mt-0.5 line-clamp-1 max-w-2xl"
          >
            {banner.description}
          </p>
        )}
      </div>
      {selectedImage && (
        <div data-testid="game-banner-lightbox" role="dialog" aria-modal="true" aria-label={`Imagen completa de ${banner.title}`} onClick={closeViewer} className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4 sm:p-8">
          <div className="relative flex max-h-full max-w-6xl items-center justify-center" onClick={(event) => event.stopPropagation()}>
            <button type="button" onClick={closeViewer} data-testid="game-banner-lightbox-close" aria-label="Cerrar imagen completa" className="absolute right-2 top-2 z-10 inline-flex items-center gap-2 border border-white/50 bg-black/75 px-3 py-2 text-xs font-bold text-white transition-[background-color] duration-200 hover:bg-black"><X className="h-4 w-4" />Cerrar</button>
            <img data-testid="game-banner-lightbox-image" src={selectedImage.url} alt={`${banner.title} ${selectedImage.index + 1} en tamaño completo`} className="max-h-[88vh] max-w-full object-contain" draggable={false} />
          </div>
        </div>
      )}
    </div>
  );
}
